import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useEffectEvent, useRef, useState } from 'react'
import { isApiError } from '../../api/client'
import { type PostReplyRequest, type QueueItemDetail, Reply } from '@rp/contracts'
import { useServiceStatusClear } from '../status/useServiceStatus'
import { useReadySession } from '../../session/useSession'
import { fetchQueueItem, queueKeys } from '../queue/queries'
import { failureGuidance } from './failure'
import { IDLE_SEND_STATE, settledState, stateAfterRefusal, stateForReply, type SendAttempt, type SendState } from './sendState'

const holderOtherThan = (item: QueueItemDetail, userId: string): string | null =>
  item.claim === null || item.claim.userId === userId ? null : item.claim.displayName

const requestBody = (item: QueueItemDetail, attempt: SendAttempt): PostReplyRequest => ({
  text: attempt.text,
  suggestionId: attempt.suggestionId,
  ...(attempt.inReplyToMessageId === null ? {} : { inReplyToMessageId: attempt.inReplyToMessageId }),
})

const isSameAttempt = (previous: SendAttempt | null, next: SendAttempt): boolean =>
  previous !== null &&
  previous.text === next.text &&
  previous.inReplyToMessageId === next.inReplyToMessageId &&
  previous.suggestionId === next.suggestionId

export const useSendReply = (item: QueueItemDetail, initialState: SendState = IDLE_SEND_STATE) => {
  const { api, me } = useReadySession()
  const queryClient = useQueryClient()
  const clearStatus = useServiceStatusClear()
  const idempotencyKeyRef = useRef(crypto.randomUUID())
  const lastAttemptRef = useRef<SendAttempt | null>(null)
  const hadTerminalFailureRef = useRef(false)
  const inFlightRef = useRef(false)
  const [state, setState] = useState<SendState>(initialState)
  const [, bumpKeyVersion] = useState(0)

  const rotateKey = () => {
    idempotencyKeyRef.current = crypto.randomUUID()
    bumpKeyVersion((version) => version + 1)
  }

  const apply = (next: SendState) => {
    hadTerminalFailureRef.current = next.kind === 'failed' || next.kind === 'rival'
    if (next.kind === 'sent') {
      clearStatus('publishing_failing')
      lastAttemptRef.current = null
      rotateKey()
    }
    setState(next)
  }

  const settled = settledState(state, item.replies)
  const settle = useEffectEvent(apply)
  useEffect(() => {
    if (settled !== state) settle(settled)
  }, [settled, state])

  const mutation = useMutation({
    mutationFn: async (attempt: SendAttempt) =>
      (
        await api.request(Reply, {
          method: 'POST',
          path: `/v1/queue/${item.id}/reply`,
          idempotencyKey: idempotencyKeyRef.current,
          body: requestBody(item, attempt),
        })
      ).data,
    onSuccess: async (reply) => {
      apply(stateForReply(reply, false) ?? { kind: 'failed', guidance: failureGuidance(reply.metaFailure, null) })
      await queryClient.invalidateQueries({ queryKey: queueKeys.all })
    },
    onError: async (error: unknown, attempt) => {
      const latest = await fetchQueueItem(api, item.id).catch(() => item)
      const apiError = isApiError(error) ? error : null
      const code = apiError?.code ?? null
      const guidance = failureGuidance(
        latest.replies.at(-1)?.metaFailure ?? null,
        code,
        apiError?.message ?? null,
        holderOtherThan(latest, me.user.id),
      )
      apply(stateAfterRefusal(latest.replies, attempt, { guidance, code, userId: me.user.id }))
      await queryClient.invalidateQueries({ queryKey: queueKeys.all })
    },
  })

  const send = (text: string, suggestionId: string | null) => {
    if (inFlightRef.current) return
    const attempt: SendAttempt = { text, inReplyToMessageId: item.latestInboundMessageId, suggestionId }
    if (hadTerminalFailureRef.current && !isSameAttempt(lastAttemptRef.current, attempt)) rotateKey()
    lastAttemptRef.current = attempt
    inFlightRef.current = true
    mutation.mutate(attempt, {
      onSettled: () => {
        inFlightRef.current = false
      },
    })
  }

  return {
    send,
    pending: mutation.isPending,
    state,
    reset: () => setState(IDLE_SEND_STATE),
    idempotencyKey: idempotencyKeyRef.current,
  }
}
